import { h, render, Component } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import Input from './input';
import { validatePersonalNumber } from '../utilities/personal-number';
import fetchJson from '../utilities/fetchJson';
import randomStr from '../utilities/random-str';

export default function BankIDAccounts(el) {
	const endpoint = el.getAttribute('data-endpoint');
	const nonce = el.getAttribute('data-nonce');
	const billectaBaseUrl = el.getAttribute('data-billecta-base-url');

	if (el) {
		render(
			<BankIDComponent
				endpoint={endpoint}
				nonce={nonce}
				billectaBaseUrl={billectaBaseUrl}
			/>,
			el
		);
	}
}

const StageInitial = Symbol('BankID Accounts Initial');
const StageRenderIframe = Symbol('BankID Accounts Render Iframe');
const StageAccountSelected = Symbol('BankID Accounts Account Selected');
const StageError = Symbol('BankID Accounts Error');

class BankIDComponent extends Component {
	state = {
		personalNumber: '',
		selectedAccount: '',
		selectedBank: null,
		stage: StageInitial,
		didInteractWithAccountPicker: false,
		loading: false,
		sessionToken: null,
		sessionId: null,
		errorMessage: '',
		errorPersonalNumber: '',
	};

	componentDidMount() {
		window.addEventListener(
			'personal-number-filled',
			this.handleFilledPersonalNumber
		);
	}

	componentWillUnmount() {
		window.removeEventListener(
			'personal-number-filled',
			this.handleFilledPersonalNumber
		);
		window.clearTimeout(this.pingTimeoutId);
	}

	render() {
		const { personalNumber } = this.state;

		let display = '';
		switch (this.state.stage) {
			case StageRenderIframe:
				display = (
					<BillectaIframe
						sessionToken={this.state.sessionToken}
						sessionId={this.state.sessionId}
						baseUrl={this.props.billectaBaseUrl}
						onAccountSelected={this.handleAccountSelected}
						onAborted={this.handleAccountSelectionAborted}
						onFailed={this.handleAccountSelectionFailed}
					/>
				);
				break;
			case StageAccountSelected:
				display = (
					<Account
						accountNumber={this.state.selectedAccount}
						bank={this.state.selectedBank}
						onChangeClick={this.handleChangeClick}
					/>
				);
				break;
			case StageError:
				display = <div>{this.state.errorMessage}</div>;
				break;
		}

		return (
			<div>
				<input
					type="hidden"
					name="selectedAccount"
					value={this.state.selectedAccount}
					data-enable-hidden-input
					required
				></input>
				{[StageInitial, StageAccountSelected].includes(this.state.stage) && (
					<div className="tabs__panel-section v2-grid">
						<div className="v2-grid__col v2-grid__col--1/1">
							<Input
								v2={true}
								label="Personnr. (ÅÅÅÅMMDDXXXX)"
								type="text"
								name="bankid-personal-number"
								pattern="\d{4}\d{2}\d{2}-?\d{4}"
								showValidation={this.state.errorPersonalNumber}
								validationMessage={this.state.errorPersonalNumber}
								required
								value={personalNumber}
								onInput={this.handleChangePersonalNumber}
								disabled={this.state.loading}
							/>
						</div>

						<div className="v2-grid__col v2-grid__col--1/1">
							<button
								className="v2-button v2-button--filled"
								type="button"
								onClick={this.handleRequestAccounts}
								disabled={
									this.state.loading ||
									this.state.stage === StageAccountSelected
								}
							>
								Välj bankkonto
							</button>
							{this.state.loading && (
								<div class="loader loader--space-before">
									<span class="loader__label">Väntar…</span>
								</div>
							)}
						</div>
					</div>
				)}
				{display}
			</div>
		);
	}

	handleFilledPersonalNumber = (ev) => {
		if (ev.detail) {
			this.setState({ personalNumber: ev.detail });
		}
	};

	handleChangePersonalNumber = (value) => {
		this.setState({ personalNumber: value });
	};

	handleRequestAccounts = (ev) => {
		ev.preventDefault();

		const validPersonalNumber = validatePersonalNumber(
			this.state.personalNumber
		);

		// Note: HTML field validation is used to show the status of the field,
		// but not to determine if the request should be sent or not.

		if (!this.state.personalNumber) {
			this.setState({ errorPersonalNumber: 'Personnummer måste fyllas i.' });
		} else if (!validPersonalNumber) {
			this.setState({
				errorPersonalNumber: 'Personnummer måste skrivas ÅÅÅÅMMDDXXXX.',
			});
		} else {
			this.setState({
				errorPersonalNumber: false,
			});
		}

		if (!validPersonalNumber) {
			return;
		}

		if (!this.state.loading) {
			this.requestBillectaSession();
		}
	};

	requestBillectaSession = async () => {
		this.setState({
			stage: StageInitial,
			loading: true,
			errorMessage: '',
			errorPersonalNumber: '',
			selectedAccount: '',
			selectedBank: null,
		});

		try {
			const { status, errorMessage, payload } = await fetchJson(
				this.props.endpoint,
				{
					method: 'post',
					body: JSON.stringify({
						PersonalIdNumber: this.state.personalNumber,
						_wpnonce: this.props.nonce,
					}),
				}
			);

			if (
				status !== 'ok' ||
				errorMessage ||
				!payload.SessionToken ||
				!payload.SessionId
			) {
				this.setState({
					loading: false,
					stage: StageError,
					errorMessage: errorMessage || 'Något gick fel, försök igen senare',
				});

				return;
			}

			this.setState({
				loading: false,
				stage: StageRenderIframe,
				sessionToken: payload.SessionToken,
				sessionId: payload.SessionId,
			});
		} catch (e) {
			this.setState({
				loading: false,
				stage: StageError,
				errorMessage: e.message || 'Något gick fel, försök igen senare',
			});
		}
	};

	handleAccountSelected = (accountNumber, selectedBank) => {
		console.log('OSS: handleAccountSelected', { accountNumber, selectedBank });

		this.setState({
			didInteractWithAccountPicker: true,
			stage: StageAccountSelected,
			selectedAccount: accountNumber,
			selectedBank,
		});
	};

	handleAccountSelectionAborted = () => {
		console.log('OSS: handleAccountSelectionAborted');

		this.setState({
			didInteractWithAccountPicker: true,
			stage: StageInitial,
			loading: false,
			selectedAccount: '',
			selectedBank: null,
			sessionToken: null,
			sessionId: null,
			errorMessage: '',
		});
	};

	handleAccountSelectionFailed = (errorMessage) => {
		console.log('OSS: handleAccountSelectionFailed', { errorMessage });

		this.setState({
			didInteractWithAccountPicker: true,
			stage: StageError,
			loading: false,
			selectedAccount: '',
			selectedBank: null,
			sessionToken: null,
			sessionId: null,
			errorMessage,
		});
	};

	handleChangeClick = () => {
		this.setState({ stage: StageInitial });
	};
}

const BillectaIframe = ({
	sessionToken,
	sessionId,
	baseUrl,
	onAccountSelected,
	onAborted,
	onFailed,
}) => {
	const iframeId = `billecta-iframe-${randomStr(8)}`;

	const [client, setClient] = useState(null);

	useEffect(() => {
		const newClient = new Billecta(sessionToken, sessionId, {
			...(baseUrl ? { baseUrl } : {}),
		});

		setClient(newClient);

		newClient.start({
			selector: `#${iframeId}`,
			width: '100%',
			maxWidth: '600px',
			colorScheme: 'light',
			onSuccessful: ({ clearingNo, accountNo, bankName }) => {
				if (onAccountSelected) {
					onAccountSelected(`${clearingNo},${accountNo}`, bankName);
				}
			},
			onAborted: () => {
				if (onAborted) {
					onAborted();
				}
			},
			onFailed: ({ errorMessage }) => {
				if (onFailed) {
					onFailed(errorMessage);
				}
			},
		});

		return () => {
			client?.stop();
		};
	}, []);

	return (
		<div className="tabs__panel-section">
			<div id={iframeId}></div>
		</div>
	);
};

const Account = ({ accountNumber, bank, onChangeClick }) => (
	<div className="tabs__panel-section">
		<p>
			Kontonummer: {accountNumber.replace(/[^0-9]/g, '-')} (
			<strong>{bank}</strong>) (
			{onChangeClick && (
				<button type="button" class="u-button-as-link" onClick={onChangeClick}>
					ändra
				</button>
			)}
			)
		</p>
	</div>
);
